if (!customElements.get('blog-search')) {
  customElements.define('blog-search', class extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-blog-search-form]');
      if (!this.form || this.events) return;

      this.input = this.form.elements.q;
      this.clearButton = this.querySelector('[data-blog-search-clear]');
      this.original = this.querySelector('[data-blog-search-original]');
      this.results = this.querySelector('[data-blog-search-results]');
      this.status = this.querySelector('[data-blog-search-status]');
      this.error = this.querySelector('[data-blog-search-error]');
      this.fallback = this.querySelector('[data-blog-search-fallback]');
      this.events = new AbortController();
      this.requestId = 0;
      const options = { signal: this.events.signal };

      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.search();
      }, options);
      this.form.addEventListener('reset', (event) => {
        event.preventDefault();
        this.input.value = '';
        this.restore();
        this.input.focus();
      }, options);
      this.input.addEventListener('input', (event) => {
        this.cancel();
        this.error.hidden = true;
        this.status.textContent = '';
        this.clearButton.hidden = !this.input.value;
        if (event.isComposing) return;
        if (!this.input.value.trim()) {
          this.restore();
          return;
        }
        this.timer = setTimeout(() => this.search(), 350);
      }, options);
      this.input.addEventListener('compositionstart', () => this.cancel(), options);
      this.input.addEventListener('compositionend', () => this.search(), options);
      this.results.addEventListener('click', (event) => {
        const link = event.target.closest('.pagination a[href]');
        if (!link || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        const url = new URL(link.href);
        this.search(url.searchParams.get('page') || '1', true);
      }, options);
      window.addEventListener('popstate', () => this.readLocation(), options);
      this.readLocation();
    }

    disconnectedCallback() {
      this.cancel();
      this.events?.abort();
      this.events = null;
    }

    cancel() {
      clearTimeout(this.timer);
      this.request?.abort();
      this.requestId += 1;
      if (this.results) this.results.setAttribute('aria-busy', 'false');
    }

    readLocation() {
      const url = new URL(window.location.href);
      this.input.value = url.searchParams.get('blog_q') || '';
      if (this.input.value.trim()) {
        this.search(url.searchParams.get('blog_page') || '1');
      } else {
        this.restore(false);
      }
    }

    updateLocation(query, page = '1') {
      const url = new URL(window.location.href);
      url.searchParams.delete('blog_q');
      url.searchParams.delete('blog_page');
      if (query) {
        url.searchParams.set('blog_q', query);
        if (String(page) !== '1') url.searchParams.set('blog_page', page);
      }
      window.history.replaceState(window.history.state, '', url);
    }

    restore(announce = true) {
      this.cancel();
      this.removeAttribute('data-search-active');
      this.original.hidden = false;
      this.results.hidden = true;
      this.results.replaceChildren();
      this.error.hidden = true;
      this.clearButton.hidden = true;
      this.status.textContent = announce ? this.dataset.restoredText : '';
      this.updateLocation('');
    }

    searchURL(query, page) {
      const url = new URL(this.form.action, window.location.origin);
      url.search = new URLSearchParams(new FormData(this.form)).toString();
      url.searchParams.set('q', query);
      url.searchParams.set('page', page);
      return url;
    }

    async search(page = '1', focusResults = false) {
      this.cancel();
      const query = this.input.value.trim();
      if (!query) {
        this.restore();
        return;
      }

      const requestId = this.requestId;
      this.request = new AbortController();
      this.clearButton.hidden = false;
      this.error.hidden = true;
      this.status.textContent = this.dataset.loadingText;
      this.results.setAttribute('aria-busy', 'true');
      const url = this.searchURL(query, page);
      this.fallback.href = url.toString();
      url.searchParams.set('section_id', 'blog-search-results');

      try {
        const response = await fetch(url, { signal: this.request.signal });
        if (!response.ok) throw new Error('Blog search request failed');
        const html = await response.text();
        if (requestId !== this.requestId) return;
        const document = new DOMParser().parseFromString(html, 'text/html');
        const content = document.querySelector('[data-blog-search-content]');
        const summary = content?.querySelector('[data-blog-search-summary]');
        if (!summary) throw new Error('Blog search section is missing');

        content.querySelectorAll('.pagination a[href]').forEach((link) => {
          const target = new URL(link.getAttribute('href'), url);
          link.href = this.searchURL(query, target.searchParams.get('page') || '1').toString();
        });
        const grid = content.querySelector('[data-blog-search-grid]');
        const originalGrid = this.original.querySelector('.main-blog__items');
        if (grid && originalGrid) grid.className = originalGrid.className.replace('main-blog__block', '').trim();

        this.results.replaceChildren(...content.childNodes);
        this.original.hidden = true;
        this.results.hidden = false;
        this.setAttribute('data-search-active', '');
        this.status.textContent = summary.textContent.trim();
        this.updateLocation(query, page);
        if (focusResults) {
          summary.focus({ preventScroll: true });
          summary.scrollIntoView({ block: 'start' });
        } else if (this.form.getBoundingClientRect().top < 0) {
          this.form.scrollIntoView({ block: 'start' });
        }
      } catch (error) {
        if (requestId !== this.requestId || error.name === 'AbortError') return;
        this.status.textContent = '';
        this.error.hidden = false;
      } finally {
        if (requestId === this.requestId) this.results.setAttribute('aria-busy', 'false');
      }
    }
  });
}
